import { useState } from 'react';
import {
  X,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  Pause,
  Download,
  Clock,
  User,
  Building,
  Calendar,
  ExternalLink,
  CheckCircle,
  RefreshCw,
  Flame,
  Thermometer,
  Snowflake,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { Call } from '../types';
import {
  formatDuration,
  formatDateTime,
  getDirectionLabel,
  getStatusLabel,
  getPhaseLabel,
} from '../utils/format';
import { api } from '../api/client';

interface CallDetailModalProps {
  call: Call;
  onClose: () => void;
  onRefresh: () => void;
}

export default function CallDetailModal({
  call,
  onClose,
  onRefresh,
}: CallDetailModalProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);

  const evaluation = call.evaluation;

  const getLeadScoreIcon = (score: number | undefined) => {
    if (score === undefined) return null;
    if (score > 25) return <Flame className="w-5 h-5 text-red-500" />;
    if (score >= 0) return <Thermometer className="w-5 h-5 text-yellow-500" />;
    return <Snowflake className="w-5 h-5 text-blue-500" />;
  };

  const handleAction = async (action: string) => {
    setLoading(action);
    try {
      switch (action) {
        case 'transcribe':
          await api.transcribeCall(call.id);
          break;
        case 'evaluate':
          await api.evaluateCall(call.id);
          break;
        case 'sync':
          await api.syncCall(call.id);
          break;
        case 'approve':
          await api.approveEvaluation(call.id);
          break;
      }
      onRefresh();
    } catch (err) {
      console.error(`Action ${action} failed:`, err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content w-full max-w-4xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-gray-100 rounded-full">
              {call.direction === 'inbound' ? (
                <PhoneIncoming className="w-6 h-6 text-green-500" />
              ) : (
                <PhoneOutgoing className="w-6 h-6 text-blue-500" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {call.company_name || call.contact_name || 'Unbekannter Anruf'}
              </h2>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>{getDirectionLabel(call.direction)}</span>
                <span>•</span>
                <span className={clsx('badge', {
                  'badge-green': call.status === 'completed',
                  'badge-yellow': call.status === 'transcribing' || call.status === 'evaluating',
                  'badge-red': call.status === 'error',
                  'badge-gray': call.status === 'pending',
                })}>
                  {getStatusLabel(call.status)}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Call Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Datum</p>
                <p className="text-sm font-medium">{formatDateTime(call.started_at)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Dauer</p>
                <p className="text-sm font-medium">{formatDuration(call.duration_seconds)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <User className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Agent</p>
                <p className="text-sm font-medium">{call.agent_name || '-'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Telefon</p>
                <p className="text-sm font-medium">{call.phone_number || '-'}</p>
              </div>
            </div>
          </div>

          {/* IDs */}
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span>Aircall ID: {call.aircall_id}</span>
            {call.hubspot_id && (
              <>
                <span>•</span>
                <a
                  href={`https://app.hubspot.com/calls/${call.hubspot_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1 text-primary-600 hover:text-primary-700"
                >
                  <span>HubSpot: {call.hubspot_id}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </>
            )}
          </div>

          {/* Audio Player */}
          {call.recording_url && (
            <div className="card p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Audio</h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="p-3 bg-primary-100 rounded-full hover:bg-primary-200 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 text-primary-600" />
                  ) : (
                    <Play className="w-6 h-6 text-primary-600" />
                  )}
                </button>
                <div className="flex-1 h-2 bg-gray-200 rounded-full">
                  <div className="w-0 h-2 bg-primary-500 rounded-full" />
                </div>
                <a
                  href={call.recording_url}
                  download
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Download className="w-5 h-5 text-gray-500" />
                </a>
              </div>
            </div>
          )}

          {/* Transcript */}
          <div className="card p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Transkript</h3>
            {call.transcript ? (
              <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
                {call.transcript}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                Keine Transkription verfugbar
              </p>
            )}
          </div>

          {/* Evaluation */}
          {evaluation && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900">Bewertung</h3>
                {evaluation.is_approved && (
                  <span className="badge badge-green">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Freigegeben
                  </span>
                )}
              </div>

              {/* Lead Score & Phase */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  {getLeadScoreIcon(evaluation.lead_score)}
                  <div>
                    <p className="text-xs text-gray-500">Leadprioritat</p>
                    <p className="text-xl font-bold">{evaluation.lead_score}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Building className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Phase</p>
                    <p className="text-lg font-semibold">
                      {getPhaseLabel(evaluation.phase)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="mb-4">
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                  Zusammenfassung
                </h4>
                <p className="text-sm text-gray-700">{evaluation.summary}</p>
              </div>

              {/* Score Reasons */}
              {evaluation.score_reasons?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Score Grunde
                  </h4>
                  <ul className="list-disc list-inside space-y-1">
                    {evaluation.score_reasons.map((reason, i) => (
                      <li key={i} className="text-sm text-gray-600">{reason}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tags */}
              {evaluation.tags?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Tags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {evaluation.tags.map((tag, i) => (
                      <span key={i} className="badge badge-blue">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              {evaluation.action_items?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Action Items
                  </h4>
                  <ul className="list-disc list-inside space-y-1">
                    {evaluation.action_items.map((item, i) => (
                      <li key={i} className="text-sm text-gray-600">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Topics */}
              {evaluation.key_topics?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                    Themen
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {evaluation.key_topics.map((topic, i) => (
                      <span key={i} className="badge badge-gray">{topic}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {!call.transcript && (
              <button
                onClick={() => handleAction('transcribe')}
                disabled={loading !== null}
                className="btn-secondary"
              >
                {loading === 'transcribe' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Transkribieren
              </button>
            )}
            {call.transcript && !evaluation && (
              <button
                onClick={() => handleAction('evaluate')}
                disabled={loading !== null}
                className="btn-secondary"
              >
                {loading === 'evaluate' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Bewerten
              </button>
            )}
            {evaluation && !evaluation.is_approved && (
              <button
                onClick={() => handleAction('approve')}
                disabled={loading !== null}
                className="btn-primary"
              >
                {loading === 'approve' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Freigeben
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {evaluation?.is_approved && !call.synced_to_hubspot_at && (
              <button
                onClick={() => handleAction('sync')}
                disabled={loading !== null}
                className="btn-primary"
              >
                {loading === 'sync' ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Zu HubSpot synchronisieren
              </button>
            )}
            {call.synced_to_hubspot_at && (
              <span className="text-sm text-green-600 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" />
                Synchronisiert
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
